import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Trash2, Factory, Save } from "lucide-react";
import { fmtINR, today } from "@/lib/format";

/**
 * BOM Issue-to-Shopfloor with conversion-cost punch panel (Iter-95).
 * Posts DR-WIP · CR-RM + DR-WIP · CR-<expense-account> for each conversion cost.
 */
export default function BOMIssue() {
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ bom_id: "", fg_item_id: "", fg_qty: 1, date: today(), notes: "", lines: [], conversion_costs: [] });

  useEffect(() => {
    (async () => {
      const [b, i, a, iss] = await Promise.all([
        api.get("/bom/boms").then((r) => r.data).catch(() => []),
        api.get("/items").then((r) => r.data).catch(() => []),
        api.get("/accounts").then((r) => r.data).catch(() => []),
        api.get("/ops/bom/issues").then((r) => r.data).catch(() => []),
      ]);
      setBoms(b); setItems(i); setAccounts(a); setIssues(iss || []);
    })();
  }, []);

  // Prefill lines when a BOM is selected
  useEffect(() => {
    if (!form.bom_id) return;
    const bom = boms.find((b) => b.id === form.bom_id);
    if (!bom) return;
    setForm((f) => ({
      ...f,
      fg_item_id: bom.fg_item_id || f.fg_item_id,
      lines: (bom.components || []).map((c) => ({
        item_id: c.item_id,
        qty: Number(c.qty || 0) * Number(f.fg_qty || 1),
        unit_cost: Number(c.unit_cost || 0),
      })),
    }));
    // eslint-disable-next-line
  }, [form.bom_id]);

  const rmValue = useMemo(() => form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0), [form.lines]);
  const convTotal = useMemo(() => (form.conversion_costs || []).reduce((s, c) => s + Number(c.amount || 0), 0), [form.conversion_costs]);
  const totalValue = rmValue + convTotal;

  const setLn = (i, patch) => setForm({ ...form, lines: form.lines.map((l, j) => j === i ? { ...l, ...patch } : l) });
  const addLn = () => setForm({ ...form, lines: [...form.lines, { item_id: "", qty: 1, unit_cost: 0 }] });
  const rmLn = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

  const setCC = (i, patch) => setForm({ ...form, conversion_costs: form.conversion_costs.map((c, j) => j === i ? { ...c, ...patch } : c) });
  const addCC = () => setForm({ ...form, conversion_costs: [...(form.conversion_costs || []), { label: "Labour", amount: 0, account_code: "" }] });
  const rmCC = (i) => setForm({ ...form, conversion_costs: form.conversion_costs.filter((_, j) => j !== i) });

  const submit = async () => {
    if (!form.bom_id) return toast.error("Select a BOM");
    if (!form.lines.length) return toast.error("Add at least one RM line");
    try {
      const r = await api.post("/ops/bom/issue", form);
      toast.success(`Issue ${r.data.issue.issue_number} · WIP DR ₹${(r.data.issue.total_value || 0).toFixed(0)}`);
      setForm({ bom_id: "", fg_item_id: "", fg_qty: 1, date: today(), notes: "", lines: [], conversion_costs: [] });
      const iss = await api.get("/ops/bom/issues").then((r) => r.data).catch(() => []);
      setIssues(iss || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bom-issue-page">
      <PageHeader eyebrow="Manufacturing · Iter-95" title="BOM Issue to Shopfloor"
        description="Records DR-WIP · CR-RM for raw materials issued to the floor, plus DR-WIP · CR-Expense for labour / overhead / utilities." />

      <Card className="rounded-sm p-4 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Issue header</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>BOM</Label>
            <Select value={form.bom_id} onValueChange={(v) => setForm({ ...form, bom_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="bi-bom"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="max-h-72">{boms.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || b.fg_item_name || b.id.slice(0, 6)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>FG qty planned</Label><Input type="number" value={form.fg_qty} onChange={(e) => setForm({ ...form, fg_qty: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" data-testid="bi-fg-qty" /></div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>
        </div>
      </Card>

      <Card className="rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// RM issued (DR WIP · CR RM · 1310 → 1320)</div>
          <Button size="sm" variant="ghost" onClick={addLn} className="h-7 gap-1"><Plus className="w-3 h-3" /> RM line</Button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40"><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Unit cost</th><th className="p-2 text-right">Value</th><th></th></tr></thead>
          <tbody>
            {form.lines.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">Pick a BOM to auto-fill or add lines manually</td></tr>}
            {form.lines.map((l, i) => (
              <tr key={i} className="border-t" data-testid={`bi-line-${i}`}>
                <td className="p-1">
                  <Select value={l.item_id} onValueChange={(v) => setLn(i, { item_id: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Pick item" /></SelectTrigger>
                    <SelectContent className="max-h-72">{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => setLn(i, { qty: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
                <td className="p-1"><Input type="number" value={l.unit_cost} onChange={(e) => setLn(i, { unit_cost: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
                <td className="p-2 text-right tabular-nums">{fmtINR(Number(l.qty || 0) * Number(l.unit_cost || 0))}</td>
                <td className="p-1 text-right"><Button size="sm" variant="ghost" onClick={() => rmLn(i)} className="h-7"><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/20"><td colSpan="3" className="p-2 text-right text-xs font-mono uppercase">RM total</td><td className="p-2 text-right font-mono tabular-nums font-semibold">{fmtINR(rmValue)}</td><td></td></tr>
          </tfoot>
        </table>
      </Card>

      <Card className="rounded-sm p-4 border-amber-500/30" data-testid="bi-cc-panel">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">// Conversion costs (Iter-95)</div>
            <div className="text-xs text-muted-foreground">Labour · overhead · utilities → adds DR-WIP · CR-&lt;expense&gt; lines in the same voucher</div>
          </div>
          <Button size="sm" variant="outline" onClick={addCC} className="rounded-sm gap-1" data-testid="bi-cc-add"><Plus className="w-3 h-3" /> Cost line</Button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <th className="p-2 text-left">Label</th>
            <th className="p-2 text-left">Expense account</th>
            <th className="p-2 text-right">Amount ₹</th>
            <th></th>
          </tr></thead>
          <tbody>
            {(form.conversion_costs || []).length === 0 && <tr><td colSpan="4" className="p-4 text-center text-muted-foreground">No conversion costs · press "Cost line" to add labour / overhead / utilities</td></tr>}
            {(form.conversion_costs || []).map((c, i) => (
              <tr key={i} className="border-t" data-testid={`bi-cc-${i}`}>
                <td className="p-1"><Input value={c.label || ""} onChange={(e) => setCC(i, { label: e.target.value })} className="rounded-sm h-8 text-xs" placeholder="Labour / Overhead / Utilities" /></td>
                <td className="p-1">
                  <Select value={c.account_code || ""} onValueChange={(v) => setCC(i, { account_code: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Expense a/c" /></SelectTrigger>
                    <SelectContent className="max-h-72">{accounts.filter((a) => String(a.code || "").startsWith("5") || String(a.code || "").startsWith("6")).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-1"><Input type="number" value={c.amount || 0} onChange={(e) => setCC(i, { amount: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`bi-cc-amount-${i}`} /></td>
                <td className="p-1 text-right"><Button size="sm" variant="ghost" onClick={() => rmCC(i)} className="h-7"><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-amber-500/10">
              <td colSpan="2" className="p-2 text-right text-xs font-mono uppercase text-amber-800">Conversion total</td>
              <td className="p-2 text-right font-mono tabular-nums font-semibold text-amber-800">{fmtINR(convTotal)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </Card>

      <Card className="rounded-sm p-4 bg-accent/5 border-accent/30 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground">// Total to be booked as WIP</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(totalValue)}</div>
          <div className="text-xs text-muted-foreground">RM ₹{rmValue.toFixed(0)} + Conversion ₹{convTotal.toFixed(0)}</div>
        </div>
        <Button onClick={submit} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="bi-submit">
          <Save className="w-4 h-4" /> Post issue voucher
        </Button>
      </Card>

      <Card className="rounded-sm p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">// Recent issues</div>
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
            <th className="p-2 text-left">Issue #</th><th className="p-2 text-left">Date</th><th className="p-2 text-right">RM ₹</th><th className="p-2 text-right">Conv ₹</th><th className="p-2 text-right">Total ₹</th><th className="p-2">Status</th>
          </tr></thead>
          <tbody>
            {issues.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-muted-foreground">No issues yet</td></tr>}
            {issues.slice(0, 10).map((i) => (
              <tr key={i.id} className="border-t cursor-pointer hover:bg-muted/40" onClick={() => setView(i)} data-testid={`bi-issue-${i.issue_number}`}>
                <td className="p-2 font-mono text-xs">{i.issue_number}</td>
                <td className="p-2 text-xs">{i.date}</td>
                <td className="p-2 text-right tabular-nums">{fmtINR(i.rm_value || 0)}</td>
                <td className="p-2 text-right tabular-nums text-amber-700">{fmtINR(i.conversion_total || 0)}</td>
                <td className="p-2 text-right tabular-nums font-medium">{fmtINR(i.total_value || 0)}</td>
                <td className="p-2 text-[10px] font-mono uppercase">{i.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Sheet open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-4 overflow-y-auto" data-testid="bi-issue-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <Factory className="w-5 h-5 text-accent" />
              BOM Issue {view?.issue_number}
              <span className="text-[10px] font-mono uppercase text-muted-foreground ml-auto">{view?.status}</span>
            </SheetTitle>
          </SheetHeader>
          {view && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Date</div><div className="mt-1 font-mono">{view.date}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">FG qty</div><div className="mt-1 text-lg tabular-nums">{view.fg_qty || "—"}</div></div>
                <div className="card-sharp p-3 bg-accent/5"><div className="text-[10px] font-mono uppercase text-accent">Total WIP DR</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(view.total_value || 0)}</div></div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// RM lines (DR WIP 1320 · CR RM 1310)</div>
                <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs"><thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Value</th></tr></thead><tbody>
                  {(view.lines || []).map((l, i) => {
                    const it = items.find((x) => x.id === l.item_id);
                    return (<tr key={i} className="border-t"><td className="p-2">{it?.name || "—"}<div className="text-[10px] font-mono text-muted-foreground">{it?.sku}</div></td><td className="p-2 text-right tabular-nums">{l.qty}</td><td className="p-2 text-right tabular-nums">{fmtINR(l.unit_cost)}</td><td className="p-2 text-right tabular-nums font-medium">{fmtINR((l.qty || 0) * (l.unit_cost || 0))}</td></tr>);
                  })}
                </tbody></table></div>
              </div>
              {(view.conversion_costs || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-amber-700 mb-2">// Conversion costs (DR WIP · CR &lt;Expense&gt;)</div>
                  <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs"><thead className="bg-amber-500/10 text-[10px] font-mono uppercase"><tr><th className="p-2 text-left">Label</th><th className="p-2 text-left">Account</th><th className="p-2 text-right">Amount</th></tr></thead><tbody>
                    {view.conversion_costs.map((c, i) => (<tr key={i} className="border-t"><td className="p-2">{c.label}</td><td className="p-2 font-mono text-[10px]">{c.account_code || "—"}</td><td className="p-2 text-right tabular-nums font-medium">{fmtINR(c.amount || 0)}</td></tr>))}
                  </tbody></table></div>
                </div>
              )}
              {view.voucher_id && <div className="border rounded-sm p-3 bg-accent/5"><div className="text-[10px] font-mono uppercase text-accent">// Linked accounting voucher</div><div className="mt-1 font-mono text-xs">{view.voucher_id}</div></div>}
              {view.notes && <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Notes</div><div className="mt-1 text-xs">{view.notes}</div></div>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
